'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { CONTACT_EMAIL, DOCS_URL } from '@/lib/constants';

import { usePrefersReducedMotion } from '../usePrefersReducedMotion';

const MONO = "'JetBrains Mono', monospace";
const HELV = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const mailto = (subject: string) =>
	`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

type Tone = 'bad' | 'warn' | 'ok';
const TONE: Record<Tone, { fg: string; bg: string; bd: string; dot: string }> =
	{
		bad: {
			fg: '#c0392b',
			bg: 'rgba(210,59,59,.12)',
			bd: 'rgba(210,59,59,.4)',
			dot: '#d23b3b',
		},
		warn: {
			fg: '#9a6a00',
			bg: 'rgba(208,148,0,.16)',
			bd: 'rgba(208,148,0,.48)',
			dot: '#d09400',
		},
		ok: {
			fg: '#127a4f',
			bg: 'rgba(21,147,95,.14)',
			bd: 'rgba(21,147,95,.45)',
			dot: '#15935f',
		},
	};

// Structural (non-translated): verdict tone + coverage matrix, parallel-indexed
// with the 8 translated pattern items.
const PATTERN_TONES: Tone[] = [
	'bad',
	'warn',
	'bad',
	'bad',
	'bad',
	'warn',
	'warn',
	'ok',
];
// 8 patterns × 8 signals (Device, Face, Doc, Phone, Geo, Behavior, Block, Net).
const COVERAGE: readonly (readonly number[])[] = [
	[1, 1, 1, 0, 0, 0, 0, 1],
	[1, 1, 0, 1, 0, 0, 0, 1],
	[1, 1, 1, 1, 1, 1, 0, 1],
	[0, 1, 1, 1, 0, 0, 0, 1],
	[1, 1, 0, 0, 0, 0, 1, 1],
	[0, 1, 0, 0, 1, 1, 0, 1],
	[1, 1, 1, 0, 0, 0, 0, 0],
	[1, 1, 1, 1, 1, 1, 0, 1],
];

interface NamedCopy {
	name: string;
	desc: string;
}
interface PatternCopy {
	name: string;
	verdict: string;
	desc: string;
	signals: string[];
}
interface Cap {
	title: string;
	sub: string;
}
interface Step {
	title: string;
	desc: string;
}

const lines = (titleLines: string[]) =>
	titleLines.map((line, i) => (
		<span key={i}>
			{i > 0 && <br />}
			{line}
		</span>
	));

export function AntifraudPage() {
	const t = useTranslations('Antifraud');
	const reducedMotion = usePrefersReducedMotion();

	const statusSteps = t.raw('hero.statusSteps') as string[];
	const metrics = t.raw('metrics') as [string, string][];
	const identityItems = t.raw('identity.items') as NamedCopy[];
	const cols = t.raw('coverage.cols') as string[];
	const caps = t.raw('coverage.caps') as Cap[];
	const patterns = t.raw('patterns.items') as PatternCopy[];
	const sendRows = t.raw('privacy.sendRows') as string[];
	const keepRows = t.raw('privacy.keepRows') as string[];
	const steps = t.raw('protection.steps') as Step[];

	const [si, setSi] = useState(0);
	useEffect(() => {
		if (reducedMotion) return;
		const id = setInterval(
			() => setSi((s) => (s + 1) % statusSteps.length),
			1900,
		);
		return () => clearInterval(id);
	}, [reducedMotion, statusSteps.length]);

	const kicker: React.CSSProperties = {
		fontFamily: MONO,
		fontSize: '11px',
		letterSpacing: '2px',
		color: 'var(--color-muted)',
	};
	const sectionWrap: React.CSSProperties = {
		maxWidth: '1340px',
		margin: '0 auto',
		padding: '44px clamp(18px,5vw,72px) 20px',
	};
	const h2: React.CSSProperties = {
		margin: 0,
		fontWeight: 400,
		fontSize: 'clamp(30px,4.6vw,52px)',
		letterSpacing: '-.03em',
		lineHeight: 1,
	};
	const lead: React.CSSProperties = {
		margin: 0,
		maxWidth: '40ch',
		fontSize: '14px',
		lineHeight: 1.6,
		color: 'var(--color-accent)',
		paddingBottom: '4px',
	};
	const darkBtn: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		padding: '16px 24px',
		background: '#f2f2f4',
		color: '#0a0a0b',
		border: 'none',
		cursor: 'pointer',
		fontFamily: MONO,
		fontSize: '12px',
		letterSpacing: '2px',
		textDecoration: 'none',
	};
	const ghostBtn: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '16px 24px',
		background: 'transparent',
		color: '#f2f2f4',
		border: '1px solid rgba(255,255,255,.3)',
		cursor: 'pointer',
		fontFamily: MONO,
		fontSize: '12px',
		letterSpacing: '2px',
		textDecoration: 'none',
	};
	const sectionHead = (
		kick: string,
		heading: React.ReactNode,
		leadText: string,
	) => (
		<>
			<div style={kicker}>{kick}</div>
			<div
				style={{
					display: 'flex',
					flexWrap: 'wrap',
					justifyContent: 'space-between',
					alignItems: 'flex-end',
					gap: '10px 20px',
					marginTop: '8px',
				}}
			>
				<h2 style={h2}>{heading}</h2>
				<p style={lead}>{leadText}</p>
			</div>
		</>
	);
	const gridBoard: React.CSSProperties = {
		display: 'grid',
		gap: '1px',
		marginTop: '24px',
		background: 'var(--color-border)',
		border: '1px solid var(--color-border)',
	};
	const card: React.CSSProperties = {
		background: 'var(--color-bg)',
		padding: '26px',
	};

	return (
		<div
			className="product-page"
			style={{
				background: 'var(--color-bg)',
				color: 'var(--color-fg)',
				fontFamily: HELV,
			}}
		>
			{/* ── HERO ── */}
			<div
				style={{
					position: 'relative',
					overflow: 'hidden',
					background: '#0a0a0b',
					color: '#f2f2f4',
					minHeight: '760px',
					display: 'flex',
					flexDirection: 'column',
					containerType: 'inline-size',
				}}
			>
				<svg
					viewBox="0 0 460 460"
					preserveAspectRatio="xMidYMid slice"
					aria-hidden="true"
					style={{
						position: 'absolute',
						top: 0,
						right: 0,
						width: 'min(72%,920px)',
						height: '100%',
					}}
				>
					<g
						stroke="rgba(255,255,255,.16)"
						strokeWidth="1"
						vectorEffect="non-scaling-stroke"
						fill="none"
					>
						<line x1="250" y1="230" x2="140" y2="120" />
						<line x1="250" y1="230" x2="360" y2="140" />
						<line x1="250" y1="230" x2="392" y2="262" />
						<line x1="250" y1="230" x2="300" y2="360" />
						<line x1="250" y1="230" x2="168" y2="330" />
						<line x1="250" y1="230" x2="92" y2="240" />
						<line x1="140" y1="120" x2="250" y2="90" />
						<line x1="360" y1="140" x2="250" y2="90" />
						<line x1="392" y1="262" x2="420" y2="360" />
						<line x1="300" y1="360" x2="420" y2="360" />
						<line x1="168" y1="330" x2="92" y2="240" />
					</g>
					<g fill="rgba(255,255,255,.55)">
						<circle className="okn-pulse" cx="140" cy="120" r="4" />
						<circle cx="360" cy="140" r="4" />
						<circle
							className="okn-pulse"
							cx="392"
							cy="262"
							r="4"
							style={{ animationDelay: '.6s' }}
						/>
						<circle cx="300" cy="360" r="4" />
						<circle
							className="okn-pulse"
							cx="168"
							cy="330"
							r="4"
							style={{ animationDelay: '1.1s' }}
						/>
						<circle cx="92" cy="240" r="4" />
						<circle cx="250" cy="90" r="3" />
						<circle cx="420" cy="360" r="3" />
					</g>
					<circle
						cx="250"
						cy="230"
						r="17"
						fill="none"
						stroke="rgba(242,242,244,.4)"
						strokeWidth="1"
					/>
					<circle cx="250" cy="230" r="7" fill="#f2f2f4" />
				</svg>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						inset: 0,
						background:
							'linear-gradient(100deg, #0a0a0b 32%, rgba(10,10,11,.6) 60%, rgba(10,10,11,.12))',
						pointerEvents: 'none',
					}}
				/>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: '1px',
						background: 'rgba(255,255,255,.14)',
						overflow: 'hidden',
					}}
				>
					<div
						className="onekyc-sweep"
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '34%',
							height: '1px',
							background:
								'linear-gradient(90deg,transparent,rgba(255,255,255,.9),transparent)',
						}}
					/>
				</div>

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						display: 'flex',
						justifyContent: 'space-between',
						gap: '16px',
						padding: 'clamp(96px,12vw,116px) clamp(18px,5vw,72px) 24px',
						fontFamily: MONO,
						fontSize: '11px',
						letterSpacing: '1.5px',
						color: 'rgba(242,242,244,.6)',
					}}
				>
					<span>ONEKYC // NETWORK_06</span>
					<span
						style={{
							border: '1px solid rgba(255,255,255,.25)',
							padding: '4px 10px',
							alignSelf: 'flex-start',
						}}
					>
						[ ANTIFRAUD ]
					</span>
				</div>

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						marginTop: 'auto',
						padding: '0 clamp(18px,5vw,72px) clamp(36px,5vw,64px)',
						maxWidth: '980px',
					}}
				>
					<div
						style={{
							fontFamily: MONO,
							fontSize: '12px',
							letterSpacing: '3px',
							color: 'rgba(242,242,244,.6)',
						}}
					>
						FRAUD NETWORK // ONEKYC-ID
					</div>
					<h1
						style={{
							margin: '14px 0 0',
							fontWeight: 400,
							fontSize: 'clamp(30px,7.6cqw,100px)',
							letterSpacing: '-.04em',
							lineHeight: 0.94,
						}}
					>
						{lines(t.raw('hero.titleLines') as string[])}
					</h1>
					<p
						style={{
							margin: '24px 0 0',
							maxWidth: '50ch',
							fontSize: 'clamp(16px,1.9vw,20px)',
							lineHeight: 1.6,
							color: 'rgba(242,242,244,.8)',
						}}
					>
						{t('hero.desc')}
					</p>
					<div
						style={{
							fontFamily: MONO,
							fontSize: '12px',
							letterSpacing: '1px',
							color: 'rgba(242,242,244,.55)',
							marginTop: '18px',
						}}
					>
						&gt; {statusSteps[si % statusSteps.length]}{' '}
						<span className="onekyc-blink" aria-hidden="true">
							_
						</span>
					</div>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10px',
							marginTop: '24px',
						}}
					>
						<a
							href={mailto('ONEKYC-ID Anti-Fraud Demo Request')}
							style={darkBtn}
						>
							{t('hero.ctaDemo')} <span aria-hidden="true">-&gt;</span>
						</a>
						<a
							href={DOCS_URL}
							target="_blank"
							rel="noopener noreferrer"
							style={ghostBtn}
						>
							{t('hero.ctaDocs')}
						</a>
					</div>
				</div>
			</div>

			{/* ── METRICS ── */}
			<div style={{ borderBottom: '1px solid var(--color-border)' }}>
				<div
					style={{
						maxWidth: '1340px',
						margin: '0 auto',
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
					}}
				>
					{metrics.map(([v, l], i) => (
						<div
							key={i}
							style={{
								padding: '28px clamp(16px,2.4vw,28px)',
								borderLeft: '1px solid var(--color-border)',
							}}
						>
							<div
								style={{
									fontFamily: MONO,
									fontSize: 'clamp(24px,3vw,38px)',
									fontWeight: 700,
									letterSpacing: '-.02em',
									lineHeight: 1,
								}}
							>
								{v}
							</div>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '10px',
									letterSpacing: '1.5px',
									color: 'var(--color-muted)',
									marginTop: '12px',
									textTransform: 'uppercase',
								}}
							>
								{l}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── SYS.02 IDENTITY ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '64px clamp(18px,5vw,72px) 20px',
				}}
			>
				{sectionHead(
					'SYS.02 // IDENTITY',
					lines(t.raw('identity.titleLines') as string[]),
					t('identity.lead'),
				)}
				<div
					style={{
						height: '1px',
						background: 'var(--color-border)',
						marginTop: '22px',
					}}
				/>
				<div
					style={{
						...gridBoard,
						gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))',
						gap: '1px',
					}}
				>
					{identityItems.map((it, i) => (
						<div key={it.name} style={card}>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '11px',
									color: 'var(--color-muted)',
								}}
							>
								{String(i + 1).padStart(2, '0')}
							</div>
							<div
								style={{
									fontSize: '16px',
									fontWeight: 600,
									letterSpacing: '-.01em',
									marginTop: '14px',
								}}
							>
								{it.name}
							</div>
							<div
								style={{
									fontSize: '13px',
									color: 'var(--color-accent)',
									lineHeight: 1.55,
									marginTop: '8px',
								}}
							>
								{it.desc}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── SYS.03 COVERAGE (matrix) ── */}
			<div style={sectionWrap}>
				{sectionHead(
					'SYS.03 // COVERAGE',
					t('coverage.title'),
					t('coverage.lead'),
				)}
				<div
					style={{
						height: '1px',
						background: 'var(--color-border)',
						margin: '22px 0 18px',
					}}
				/>
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '12px',
						marginBottom: '14px',
					}}
				>
					<p
						style={{ margin: 0, fontSize: '13px', color: 'var(--color-muted)' }}
					>
						{t('coverage.subtitle')}
					</p>
					<span
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: '8px',
							fontFamily: MONO,
							fontSize: '11px',
							color: '#127a4f',
							background: 'rgba(21,147,95,.1)',
							border: '1px solid rgba(21,147,95,.32)',
							padding: '7px 12px',
						}}
					>
						<span aria-hidden="true">✓</span>
						{t('coverage.badge')}
					</span>
				</div>
				<div
					style={{
						border: '1px solid var(--color-border)',
						background: 'var(--color-bg)',
						overflowX: 'auto',
					}}
				>
					<div style={{ minWidth: '860px' }}>
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'minmax(180px,1.7fr) repeat(8, 1fr) 76px',
								padding: '12px 16px',
								background: '#0f0f10',
								color: '#fff',
								fontFamily: MONO,
								fontSize: '9.5px',
								letterSpacing: '.5px',
							}}
						>
							<span
								style={{
									textTransform: 'uppercase',
									color: 'rgba(255,255,255,.55)',
								}}
							>
								{t('coverage.colHeader')}
							</span>
							{cols.map((c) => (
								<span
									key={c}
									style={{ textAlign: 'center', color: 'rgba(255,255,255,.8)' }}
								>
									{c}
								</span>
							))}
							<span
								style={{
									textAlign: 'center',
									textTransform: 'uppercase',
									color: 'rgba(255,255,255,.55)',
								}}
							>
								{t('coverage.verdictHeader')}
							</span>
						</div>
						{patterns.map((p, ri) => {
							const tone = TONE[PATTERN_TONES[ri]];
							return (
								<div
									key={p.name}
									style={{
										display: 'grid',
										gridTemplateColumns:
											'minmax(180px,1.7fr) repeat(8, 1fr) 76px',
										alignItems: 'center',
										padding: '13px 16px',
										borderBottom: '1px solid var(--color-border)',
									}}
								>
									<span
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '9px',
											minWidth: 0,
										}}
									>
										<span
											style={{
												width: '4px',
												height: '22px',
												flex: '0 0 auto',
												background: tone.dot,
											}}
										/>
										<span
											style={{
												fontSize: '13px',
												fontWeight: 600,
												letterSpacing: '-.01em',
											}}
										>
											{p.name}
										</span>
									</span>
									{COVERAGE[ri].map((on, ci) => (
										<span
											key={ci}
											style={{ display: 'flex', justifyContent: 'center' }}
										>
											<span
												style={
													on
														? {
																width: '9px',
																height: '9px',
																borderRadius: '50%',
																background: 'var(--color-fg)',
															}
														: {
																width: '9px',
																height: '9px',
																borderRadius: '50%',
																border: '1px solid var(--color-border-hover)',
															}
												}
											/>
										</span>
									))}
									<span style={{ display: 'flex', justifyContent: 'center' }}>
										<span
											style={{
												fontFamily: MONO,
												fontSize: '9.5px',
												letterSpacing: '.5px',
												padding: '4px 8px',
												color: tone.fg,
												background: tone.bg,
												border: `1px solid ${tone.bd}`,
											}}
										>
											{p.verdict}
										</span>
									</span>
								</div>
							);
						})}
					</div>
				</div>
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: '16px',
						marginTop: '10px',
						fontFamily: MONO,
						fontSize: '10.5px',
						color: 'var(--color-muted)',
					}}
				>
					<span
						style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}
					>
						<span
							style={{
								width: '9px',
								height: '9px',
								borderRadius: '50%',
								background: 'var(--color-fg)',
							}}
						/>
						{t('coverage.legendOn')}
					</span>
					<span
						style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}
					>
						<span
							style={{
								width: '9px',
								height: '9px',
								borderRadius: '50%',
								border: '1px solid var(--color-border-hover)',
							}}
						/>
						{t('coverage.legendOff')}
					</span>
				</div>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,330px),1fr))',
						gap: '10px',
						marginTop: '22px',
					}}
				>
					{caps.map((c) => (
						<div
							key={c.title}
							style={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: '11px',
								border: '1px solid var(--color-border)',
								background: 'var(--color-bg)',
								padding: '13px 15px',
							}}
						>
							<span
								style={{
									width: '22px',
									height: '22px',
									flex: '0 0 auto',
									borderRadius: '50%',
									background: 'var(--color-fg)',
									color: 'var(--color-bg)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '12px',
								}}
								aria-hidden="true"
							>
								✓
							</span>
							<div>
								<div
									style={{
										fontSize: '13.5px',
										fontWeight: 600,
										letterSpacing: '-.01em',
									}}
								>
									{c.title}
								</div>
								<div
									style={{
										fontSize: '12px',
										color: 'var(--color-muted)',
										lineHeight: 1.5,
										marginTop: '3px',
									}}
								>
									{c.sub}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── SYS.04 PATTERNS ── */}
			<div style={sectionWrap}>
				{sectionHead(
					'SYS.04 // PATTERNS',
					t('patterns.title'),
					t('patterns.lead'),
				)}
				<div
					style={{
						...gridBoard,
						gridTemplateColumns:
							'repeat(auto-fill,minmax(min(100%,290px),1fr))',
					}}
				>
					{patterns.map((p, i) => {
						const tone = TONE[PATTERN_TONES[i]];
						return (
							<div
								key={p.name}
								style={{
									...card,
									padding: '22px',
									display: 'flex',
									flexDirection: 'column',
									gap: '12px',
									borderTop: `3px solid ${tone.fg}`,
								}}
							>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: '10px',
									}}
								>
									<span
										style={{
											fontFamily: MONO,
											fontSize: '11px',
											color: 'var(--color-muted)',
										}}
									>
										{String(i + 1).padStart(2, '0')}
									</span>
									<span
										style={{
											fontFamily: MONO,
											fontSize: '9.5px',
											letterSpacing: '.5px',
											padding: '4px 9px',
											color: tone.fg,
											background: tone.bg,
											border: `1px solid ${tone.bd}`,
											flex: '0 0 auto',
										}}
									>
										{p.verdict}
									</span>
								</div>
								<div
									style={{
										fontSize: '17px',
										fontWeight: 600,
										letterSpacing: '-.012em',
									}}
								>
									{p.name}
								</div>
								<div
									style={{
										fontSize: '13px',
										color: 'var(--color-accent)',
										lineHeight: 1.55,
										flex: '1 1 auto',
									}}
								>
									{p.desc}
								</div>
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
									{p.signals.map((s) => (
										<span
											key={s}
											style={{
												fontFamily: MONO,
												fontSize: '10px',
												letterSpacing: '.3px',
												padding: '3px 8px',
												background: 'var(--color-surface)',
												color: 'var(--color-muted)',
												border: '1px solid var(--color-border)',
											}}
										>
											{s}
										</span>
									))}
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* ── SYS.05 PRIVACY ── */}
			<div style={sectionWrap}>
				{sectionHead(
					'SYS.05 // PRIVACY',
					lines(t.raw('privacy.titleLines') as string[]),
					t('privacy.lead'),
				)}
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))',
						gap: '16px',
						marginTop: '24px',
					}}
				>
					<div
						style={{
							border: '1px solid var(--color-border)',
							background: 'var(--color-bg)',
							padding: '24px',
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '9px',
								fontFamily: MONO,
								fontSize: '10px',
								letterSpacing: '1px',
								color: '#127a4f',
							}}
						>
							<span aria-hidden="true">◉</span>
							{t('privacy.sendLabel')}
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '1px',
								marginTop: '16px',
							}}
						>
							{sendRows.map((r) => (
								<div
									key={r}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '10px',
										padding: '11px 2px',
										borderTop: '1px solid var(--color-border-subtle)',
										fontSize: '13.5px',
										color: 'var(--color-accent)',
									}}
								>
									<span
										style={{ color: '#127a4f', flex: '0 0 auto' }}
										aria-hidden="true"
									>
										✓
									</span>
									{r}
								</div>
							))}
						</div>
					</div>
					<div
						style={{
							border: '1px solid #0b0b0c',
							background: '#0a0a0b',
							color: '#f2f2f4',
							padding: '24px',
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '9px',
								fontFamily: MONO,
								fontSize: '10px',
								letterSpacing: '1px',
								color: 'rgba(242,242,244,.7)',
							}}
						>
							<span aria-hidden="true">🔒</span>
							{t('privacy.keepLabel')}
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '1px',
								marginTop: '16px',
							}}
						>
							{keepRows.map((r) => (
								<div
									key={r}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '10px',
										padding: '11px 2px',
										borderTop: '1px solid rgba(255,255,255,.1)',
										fontSize: '13.5px',
										color: 'rgba(242,242,244,.86)',
									}}
								>
									<span
										style={{ color: 'rgba(242,242,244,.5)', flex: '0 0 auto' }}
										aria-hidden="true"
									>
										•
									</span>
									{r}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* ── SYS.06 PROTECTION ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '44px clamp(18px,5vw,72px) 64px',
				}}
			>
				{sectionHead(
					'SYS.06 // PROTECTION',
					lines(t.raw('protection.titleLines') as string[]),
					t('protection.lead'),
				)}
				<div
					style={{
						...gridBoard,
						gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
					}}
				>
					{steps.map((s, i) => (
						<div key={s.title} style={card}>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '34px',
									fontWeight: 700,
									letterSpacing: '-.03em',
									lineHeight: 1,
								}}
							>
								{String(i + 1).padStart(2, '0')}
							</div>
							<div
								style={{
									fontSize: '17px',
									fontWeight: 600,
									letterSpacing: '-.012em',
									marginTop: '16px',
								}}
							>
								{s.title}
							</div>
							<div
								style={{
									fontSize: '13px',
									color: 'var(--color-accent)',
									lineHeight: 1.55,
									marginTop: '8px',
								}}
							>
								{s.desc}
							</div>
						</div>
					))}
				</div>
				<div
					style={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: '10px',
						marginTop: '14px',
						padding: '14px 16px',
						background: 'var(--color-bg)',
						border: '1px solid var(--color-border)',
						fontSize: '13px',
						color: 'var(--color-accent)',
						lineHeight: 1.55,
					}}
				>
					<span aria-hidden="true" style={{ flex: '0 0 auto' }}>
						🛡
					</span>
					<span>
						<b style={{ fontWeight: 600, color: 'var(--color-fg)' }}>
							{t('protection.noteBold')}
						</b>{' '}
						{t('protection.noteText')}
					</span>
				</div>
			</div>

			{/* ── CTA (dark) ── */}
			<div style={{ background: '#0a0a0b', color: '#f2f2f4' }}>
				<div
					style={{
						maxWidth: '1340px',
						margin: '0 auto',
						padding: 'clamp(48px,6vw,80px) clamp(18px,5vw,72px)',
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'flex-end',
						justifyContent: 'space-between',
						gap: '24px',
					}}
				>
					<div>
						<div
							style={{
								fontFamily: MONO,
								fontSize: '11px',
								letterSpacing: '2px',
								color: 'rgba(242,242,244,.5)',
							}}
						>
							{'// JOIN_THE_NETWORK'}
						</div>
						<h2
							style={{
								margin: '14px 0 0',
								fontWeight: 400,
								fontSize: 'clamp(28px,4.4vw,52px)',
								letterSpacing: '-.03em',
								lineHeight: 1.02,
							}}
						>
							{lines(t.raw('cta.titleLines') as string[])}
						</h2>
						<p
							style={{
								margin: '18px 0 0',
								maxWidth: '48ch',
								fontSize: '15px',
								lineHeight: 1.6,
								color: 'rgba(242,242,244,.75)',
							}}
						>
							{t('cta.desc')}
						</p>
					</div>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
						<a
							href={mailto('ONEKYC-ID Anti-Fraud Demo Request')}
							style={darkBtn}
						>
							{t('cta.ctaDemo')} <span aria-hidden="true">-&gt;</span>
						</a>
						<a href={mailto('ONEKYC-ID Anti-Fraud Network')} style={ghostBtn}>
							{t('cta.ctaContact')}
						</a>
					</div>
				</div>
			</div>
		</div>
	);
}
